import React, { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { RoutineAction } from '../../../models';

interface SimpleTextFormProps {
    action: RoutineAction;
    onSave?: (action: RoutineAction) => void;
    fields: { key: string; labelKey: string; multiline?: boolean; placeholder?: string }[];
}

function SimpleTextForm(props: SimpleTextFormProps) {
    const { action, onSave, fields } = props;
    const { t } = useTranslation();
    const [options, setOptions] = useState<AnyObj>(action.options || {});

    useEffect(() => {
        onSave && onSave({ ...action, options });
    }, [options]);

    return (
        <div className="flex flex-col space-y-2 w-full">
            {fields.map((field) => (
                <div key={field.key} className="grid grid-cols-1 xl:grid-cols-4 gap-2 bg-purple-50 dark:bg-purple-950/30 p-2 border rounded">
                    <label className="text-sm col-span-1">{t(field.labelKey)}</label>
                    <div className="col-span-3">
                        {field.multiline ? (
                            <textarea
                                className="text-sm border rounded border-gray-300 dark:border-gray-600 w-full"
                                rows={3}
                                value={options[field.key] || ''}
                                placeholder={field.placeholder || ''}
                                onChange={(e) => setOptions({ ...options, [field.key]: e.target.value })}
                            />
                        ) : (
                            <input
                                type="text"
                                className="text-sm border rounded border-gray-300 dark:border-gray-600 w-full"
                                value={options[field.key] || ''}
                                placeholder={field.placeholder || ''}
                                onChange={(e) => setOptions({ ...options, [field.key]: e.target.value })}
                            />
                        )}
                    </div>
                </div>
            ))}
        </div>
    );
}

export function AddCommentForm(props: { action: RoutineAction; onSave?: (action: RoutineAction) => void }) {
    return (
        <SimpleTextForm
            {...props}
            fields={[{ key: 'message', labelKey: 'projects.routine.action.comment_message', multiline: true }]}
        />
    );
}

export function SendEmailForm(props: { action: RoutineAction; onSave?: (action: RoutineAction) => void }) {
    return (
        <SimpleTextForm
            {...props}
            fields={[
                { key: 'to', labelKey: 'projects.routine.action.email_to', placeholder: 'requester' },
                { key: 'subject', labelKey: 'projects.routine.action.email_subject' },
                { key: 'body', labelKey: 'projects.routine.action.email_body', multiline: true },
            ]}
        />
    );
}

export function SetEstimateForm(props: { action: RoutineAction; onSave?: (action: RoutineAction) => void }) {
    const { action, onSave } = props;
    const { t } = useTranslation();
    const [options, setOptions] = useState<AnyObj>(action.options || {});
    useEffect(() => { onSave && onSave({ ...action, options }); }, [options]);

    return (
        <div className="grid grid-cols-1 xl:grid-cols-4 gap-2 bg-purple-50 dark:bg-purple-950/30 p-2 border rounded w-full">
            <label className="text-sm col-span-1">{t('projects.routine.action.change_estimate')}</label>
            <div className="col-span-3">
                <input
                    type="number"
                    min={0}
                    step={0.5}
                    className="text-sm border rounded border-gray-300 dark:border-gray-600 w-full"
                    value={options.estimate ?? ''}
                    onChange={(e) => setOptions({ ...options, estimate: Number(e.target.value) })}
                />
            </div>
        </div>
    );
}

export function DiscardForm() {
    const { t } = useTranslation();
    return (
        <div className="text-sm text-gray-700 dark:text-gray-200 p-2 bg-purple-50 dark:bg-purple-950/30 border rounded w-full">
            {t('projects.routine.action.discard_desc')}
        </div>
    );
}

export default SimpleTextForm;
